import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  ListSubheader,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import styles from "./Header.module.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { menuItems, submenuData } from "../contents/submenuData";
import Fade from "@mui/material/Fade";
import { scrollToTop } from "../utils/scroll";

function Header({ theme, toggleTheme, isVisible, openContactForm }) {
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const isMobile = useMediaQuery("(max-width:768px)");
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavItemClick = (e, item) => {
    e.preventDefault();
    if (!isMobile && item.link) {
      setIsMenuOpen(false);
      setActiveSubmenu(null);
      setHoveredItem(null);
      navigate(item.link);
      return;
    }
    setIsMenuOpen(false);
    setActiveSubmenu(null);
    setHoveredItem(null);
  };

  const handleMouseEnter = (menuKey) => {
    setIsMenuOpen(true);
    setActiveSubmenu(menuKey);
    setHoveredItem(menuKey);
  };

  const handleMouseLeave = () => {
    setIsMenuOpen(false);
    setActiveSubmenu(null);
    setHoveredItem(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.classList.toggle("body-blur", !isMobileMenuOpen);
    document.body.classList.toggle("mobile-menu-open", !isMobileMenuOpen);
    document.body.style.overflow = isMobileMenuOpen ? "auto" : "hidden";
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove("body-blur");
    document.body.classList.remove("mobile-menu-open");
    document.body.style.overflow = "auto";
  };

  useEffect(() => {
    const mainContent = document.querySelector("main");
    if (isMenuOpen) {
      mainContent.classList.add("blur");
      document.body.style.overflow = "hidden";
      document.body.classList.add("submenu-open");
    } else {
      mainContent.classList.remove("blur");
      document.body.style.overflow = "";
      document.body.classList.remove("submenu-open");
    }
  }, [isMenuOpen]);

  const handleSubmenuItemClick = (item) => {
    if (item.action === "contact") {
      openContactForm();
    }
    closeMobileMenu();
    setIsMenuOpen(false);
  };

  const renderSubmenuContent = (submenuKey) => {
    const submenu = submenuData[submenuKey];
    if (!submenu) return null;

    return (
      <>
        {submenu.map((column, index) => (
          <div key={index} className={styles.submenuColumn}>
            <h3>{column.title}</h3>
            <ul>
              {column.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <Link
                    to={item.link || "#"}
                    onClick={() => handleSubmenuItemClick(item)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    );
  };

  const renderMobileSubmenu = (submenuKey) => {
    const submenu = submenuData[submenuKey];
    if (!submenu) return null;

    return (
      <Collapse in={activeSubmenu === submenuKey} timeout="auto" unmountOnExit>
        {submenu.map((column, index) => (
          <List component="div" disablePadding key={index}>
            <ListSubheader>{column.title}</ListSubheader>
            {column.items.map((item, itemIndex) => (
              <ListItem button key={itemIndex} onClick={closeMobileMenu}>
                <ListItemText>
                  <Link
                    to={item.link || "#"}
                    onClick={() => handleSubmenuItemClick(item)}
                  >
                    {item.name}
                  </Link>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        ))}
      </Collapse>
    );
  };

  const renderDesktopMenu = () => (
    <nav className={styles.navContainer}>
      <ul className={styles.navList}>
        {menuItems.map((item) => (
          <li
            key={item.key}
            className={`${styles.navItem} ${
              hoveredItem && hoveredItem !== item.key ? styles.dimmed : ""
            }`}
            onMouseEnter={() => handleMouseEnter(item.key)}
            onClick={(e) => handleNavItemClick(e, item)}
          >
            <span className={styles.navLink}>{item.name}</span>
          </li>
        ))}
      </ul>
    </nav>
  );

  const renderMobileMenu = () => (
    <Fade in={isMobileMenuOpen}>
      <div className={styles.mobileDrawer}>
        <List component="nav">
          {menuItems.map((item) => (
            <div key={item.key}>
              <ListItem
                button
                onClick={() => {
                  if (item.link) {
                    navigate(item.link);
                    closeMobileMenu();
                    return;
                  }
                  setActiveSubmenu(
                    activeSubmenu === item.key ? null : item.key
                  );
                }}
              >
                <ListItemText primary={item.name} />
              </ListItem>
              {renderMobileSubmenu(item.key)}
            </div>
          ))}
        </List>
      </div>
    </Fade>
  );

  return (
    <div
      className={`${styles.headerWrapper} ${
        isMenuOpen ? styles.expanded : ""
      } ${!isVisible ? styles.hidden : ""}`}
      onMouseLeave={handleMouseLeave}
    >
      <header
        className={`${styles.header} ${
          isMenuOpen ? styles.submenuVisible : ""
        }`}
      >
        <div className={styles.headerContent}>
          <Link
            to="/"
            className={`${styles.logo} ${styles.logoLink}`}
            onClick={(e) => {
              scrollToTop();
              setIsMenuOpen(false);
              setActiveSubmenu(null);
              setHoveredItem(null);
              if (isMobile && isMobileMenuOpen) {
                closeMobileMenu();
              }
            }}
          >
            [<span className={styles.logoAccent}>d</span>]
            <span className={styles.logoWord}>atacook</span>
          </Link>
          {isMobile ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileMenu}
              className={`${styles.hamburger} ${
                isMobileMenuOpen ? styles.closeIcon : ""
              }`}
            >
              {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          ) : (
            location.pathname !== "/dashboard" && renderDesktopMenu()
          )}
          {!isMobile && (
            <button className={styles.ctaButton} onClick={openContactForm}>
              Loslegen
            </button>
          )}
        </div>
      </header>
      {isMenuOpen && activeSubmenu && (
        <div className={`${styles.submenuPanel} ${styles.visible}`}>
          <div className={styles.submenuContent} key={activeSubmenu}>
            {renderSubmenuContent(activeSubmenu)}
          </div>
        </div>
      )}
      {isMobile && renderMobileMenu()}
    </div>
  );
}

export default Header;
